// pinned-filter.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

interface PinnedFilterStore {
  leads: any[];
  pipeline: any[];
}

interface ActivePinnedFilterStore {
  leads: any | null;
  pipeline: any | null;
}

@Injectable({
  providedIn: 'root'
})
export class PinnedFiltersService {
  // Separate stores for leads and pipeline
  private pinnedFiltersSubject = new BehaviorSubject<PinnedFilterStore>({ 
    leads: [], 
    pipeline: [] 
  });
  
  // Make the main observable return the entire store
  public pinnedFilters$: Observable<PinnedFilterStore> = this.pinnedFiltersSubject.asObservable();
  
  // Separate observables for each view
  public leadsPinnedFilters$: Observable<any[]> = new Observable(observer => {
    this.pinnedFilters$.subscribe(store => {
      observer.next(store.leads);
    });
  });
  
  public pipelinePinnedFilters$: Observable<any[]> = new Observable(observer => {
    this.pinnedFilters$.subscribe(store => {
      observer.next(store.pipeline);
    });
  });
  
  private activePinnedFilterSubject = new BehaviorSubject<ActivePinnedFilterStore>({ 
    leads: null, 
    pipeline: null 
  });
  
  public activePinnedFilter$: Observable<ActivePinnedFilterStore> = this.activePinnedFilterSubject.asObservable();
  
  // Separate observables for active filters
  public leadsActivePinnedFilter$: Observable<any> = new Observable(observer => {
    this.activePinnedFilter$.subscribe(store => observer.next(store.leads));
  });
  
  public pipelineActivePinnedFilter$: Observable<any> = new Observable(observer => {
    this.activePinnedFilter$.subscribe(store => observer.next(store.pipeline));
  });
  
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$: Observable<boolean> = this.isLoadingSubject.asObservable();
  
  private pinnedFiltersLoaded = { 
    leads: false, 
    pipeline: false 
  };

  constructor() {
  }

  private sortFiltersBySequence(filters: any[]): any[] {
    return [...filters].sort((a, b) => {
      // First sort by sequence
      const seqA = a.sequence ?? 0;
      const seqB = b.sequence ?? 0;
      
      if (seqA !== seqB) {
        return seqA - seqB;
      }
      
      // Fallback to ID if sequences are equal
      return a.id - b.id;
    });
  }
  
  // Update updatePinnedFilters method
  updatePinnedFilters(filters: any[], viewType: 'leads' | 'pipeline' = 'leads'): void {
    const uniqueFilters = this.removeDuplicateFilters(filters);
    const sortedFilters = this.sortFiltersBySequence(uniqueFilters); // Changed from sortFiltersByID
    
    const currentStore = this.pinnedFiltersSubject.value;
    this.pinnedFiltersSubject.next({
      ...currentStore,
      [viewType]: sortedFilters
    });
    this.pinnedFiltersLoaded[viewType] = true;
    
    const selectedFilter = sortedFilters.find(f => f.is_selected);
    if (selectedFilter) {
      this.setActivePinnedFilter(selectedFilter, viewType);
    } else {
      this.setActivePinnedFilter(null, viewType);
    }
  }
  
  // Add new method for reordering
  reorderFilters(reorderedFilters: any[], viewType: 'leads' | 'pipeline' = 'leads'): void {
    const currentStore = this.pinnedFiltersSubject.value;
    this.pinnedFiltersSubject.next({
      ...currentStore,
      [viewType]: reorderedFilters
    });
  }

  // Helper method to remove duplicate filters
  private removeDuplicateFilters(filters: any[]): any[] {
    const uniqueMap = new Map();
    
    filters.forEach(filter => {
      // Use the filter ID as the key
      const key = filter.id;
      
      // If we haven't seen this ID yet, or if this one is newer, keep it
      if (!uniqueMap.has(key) || new Date(filter.updated_at) > new Date(uniqueMap.get(key).updated_at)) {
        uniqueMap.set(key, filter);
      }
    });
    
    return Array.from(uniqueMap.values());
  }

  // Get current pinned filters for specific view
  getPinnedFilters(viewType: 'leads' | 'pipeline' = 'leads'): any[] {
    const filters = this.pinnedFiltersSubject.value[viewType];
    return filters;
  }

  // Add a filter to pinned for specific view
  addPinnedFilter(filter: any, viewType: 'leads' | 'pipeline' = 'leads'): void {
    const current = this.getPinnedFilters(viewType);
    if (!current.find(f => f.id === filter.id)) {
      const updatedFilters = [...current, filter];
      this.updatePinnedFilters(updatedFilters, viewType);
    } else {
    }
  }

  // Remove a filter from pinned for specific view
  removePinnedFilter(filterId: number, viewType: 'leads' | 'pipeline' = 'leads'): void {
    const current = this.getPinnedFilters(viewType);
    const updatedFilters = current.filter(f => f.id !== filterId);
    
    const currentActive = this.activePinnedFilterSubject.value[viewType];
    if (currentActive?.id === filterId) {
      this.setActivePinnedFilter(null, viewType);
    }
    
    const currentStore = this.pinnedFiltersSubject.value;
    this.pinnedFiltersSubject.next({
      ...currentStore,
      [viewType]: updatedFilters
    });
  }

  // Set active pinned filter for specific view
  setActivePinnedFilter(filter: any, viewType: 'leads' | 'pipeline' = 'leads'): void {
    const currentStore = this.activePinnedFilterSubject.value;
    
    this.activePinnedFilterSubject.next({
      ...currentStore,
      [viewType]: filter
    });
    
    if (filter) {
      const current = this.getPinnedFilters(viewType);
      const updated = current.map(f => ({
        ...f,
        is_selected: f.id === filter.id
      }));
      
      const pinnedStore = this.pinnedFiltersSubject.value;
      this.pinnedFiltersSubject.next({
        ...pinnedStore,
        [viewType]: updated
      });
    }
  }

  // Get active pinned filter for specific view
  getActivePinnedFilter(viewType: 'leads' | 'pipeline' = 'leads'): any {
    return this.activePinnedFilterSubject.value[viewType];
  }

  // Clear all pinned filters for specific view
  clearPinnedFilters(viewType: 'leads' | 'pipeline' = 'leads'): void {
    this.updatePinnedFilters([], viewType);
    this.setActivePinnedFilter(null, viewType);
    this.pinnedFiltersLoaded[viewType] = false;
  }

  // Check if pinned filters have been loaded for specific view
  isPinnedFiltersLoaded(viewType: 'leads' | 'pipeline' = 'leads'): boolean {
    return this.pinnedFiltersLoaded[viewType];
  }

  setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }
  
  // NEW METHOD: Clear all data for all views (called on logout)
  clearAllData(): void {
    // Reset the main store
    this.pinnedFiltersSubject.next({ 
      leads: [], 
      pipeline: [] 
    });
    
    // Reset active filters
    this.activePinnedFilterSubject.next({ 
      leads: null, 
      pipeline: null 
    });
    
    // Reset loaded flags
    this.pinnedFiltersLoaded = { 
      leads: false, 
      pipeline: false 
    };
    
    // Reset loading state
    this.isLoadingSubject.next(false);
  }
  
  // Backward compatibility methods (default to 'leads')
  setViewType(viewType: 'leads' | 'pipeline'): void {
    // This is now a no-op as we handle views separately
  }
  
  getViewType(): 'leads' | 'pipeline' {
    // Return 'leads' for backward compatibility
    return 'leads';
  }
}